/* eslint-disable react/no-unused-prop-types */
import { Box } from '@mui/material';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import UPButton from 'components/button';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import { RowSeverity, UPTanStackTable, UPTanStackTableColumn } from 'components/tanstack-table';
import { CompanyRole } from 'constants/CompanyRole';
import { useGridPagination } from 'hooks/useGridPagination';
import { useGridSearch } from 'hooks/useGridSearch';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'store';
import { getFunctionDescriptions } from 'store/actions/company-wage-exemptions-actions';
import { getParitairComiteSelectItemsByCompanyAction } from 'store/actions/paritair-comite-actions';
import { functionsDescriptionSelector } from 'store/selectors/company-wage-exemptions-selectors';
import { paritairComiteByCompanySelectItemsSelector } from 'store/selectors/paritair-comite-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  COMPANY_PAYROLL__ADD_NEW_FUNCTION_EXEMPTION,
  COMPANY_PAYROLL__FUNCTION,
  COMPANY_PAYROLL__PARITAIR_COMITE,
  COMPANY_PAYROLL__WAGE_EXEMPTION,
  DATAGRID__NO_ROWS,
  GENERAL__SELECT,
  WAGE_INDEXATION__VALID_FROM,
  WAGE_INDEXATION__VALID_TO,
} from 'translations/constants';
import IFunctionExemption from 'types/company/payroll/IFunctionExemption';
import { FunctionExemptionsGridProps, WageExemptionField } from '../types';
import { DeleteActionCell, WageInputCell } from './cells';
import { isUnsavedWageExemptionRow } from './helpers';

const FunctionExemptionsGrid = (props: FunctionExemptionsGridProps) => {
  const { functionExemptions, setFunctionExemptions, rowErrors } = props;

  const [t] = useTranslation();
  const { companyId } = useParams();
  const dispatch = useDispatch();

  const userIsCompanyManager = useAppSelector(profileSelector).role === CompanyRole.COMPANY_MANAGER;
  const functions = useAppSelector(functionsDescriptionSelector);
  const paritairComites = useAppSelector(paritairComiteByCompanySelectItemsSelector);

  // Derived from the function list alone, never from the rows. Recomputing these per keystroke rebuilt the
  // columns array on every edit, and options already used were disabled outright — but a function may
  // legitimately be exempted again under a different joint commission, which is the level uniqueness is
  // actually enforced at.
  const functionOptions = useMemo<UPDropdownItem[]>(
    () => (functions ?? []).map(func => ({ value: func.id.toString(), label: func.description })),
    [functions],
  );

  useEffect(() => {
    dispatch(getFunctionDescriptions(toNumber(companyId)));
  }, [companyId, dispatch]);

  useEffect(() => {
    dispatch(getParitairComiteSelectItemsByCompanyAction(toNumber(companyId)));
  }, [companyId, dispatch]);

  const matches = useCallback(
    (row: IFunctionExemption, term: string) => {
      const needle = term.toLowerCase();
      const functionLabel = functionOptions.find(o => o.value === row.functionId?.toString())?.label ?? '';
      const paritairLabel = paritairComites?.find(p => p.value === row.paritairComiteId)?.label ?? '';
      const startDate = (row.startDate ?? '').toString();
      const endDate = (row.endDate ?? '').toString();
      return (
        functionLabel.toLowerCase().includes(needle) ||
        paritairLabel.toLowerCase().includes(needle) ||
        (row.wageExemption?.toString() ?? '').toLowerCase().includes(needle) ||
        startDate.toLowerCase().includes(needle) ||
        endDate.toLowerCase().includes(needle)
      );
    },
    [functionOptions, paritairComites],
  );

  const isUnsavedRow = useCallback((row: IFunctionExemption) => isUnsavedWageExemptionRow(row), []);
  const { onSearch, filteredRows } = useGridSearch(functionExemptions, matches, isUnsavedRow);
  const getRowId = useCallback((row: IFunctionExemption) => row.id, []);
  const { pagination, setPagination, revealRow, resetToFirstPage, scrollToRowId } = useGridPagination(
    filteredRows,
    getRowId,
  );

  // A narrower term can leave fewer pages than the one being viewed, and the first match is the one worth
  // showing anyway.
  const onSearchChange = useCallback(
    (value: string) => {
      onSearch(value);
      resetToFirstPage();
    },
    [onSearch, resetToFirstPage],
  );

  const updateFunctionExemption = useCallback(
    (id, field, value) => {
      setFunctionExemptions(prev => {
        const index = prev.findIndex(w => w.id === id);
        if (index < 0) return prev;
        const updated = { ...prev[index], [field]: value };
        return [...prev.slice(0, index), updated, ...prev.slice(index + 1)];
      });
    },
    [setFunctionExemptions],
  );

  const onWageChange = useCallback(
    (id: number, value: number) => updateFunctionExemption(id, 'wageExemption', value),
    [updateFunctionExemption],
  );

  const onAddNewFunctionWageExemption = useCallback(() => {
    const id = Math.random();
    setFunctionExemptions(prev => [{ id, functionId: '', wageExemption: 0, paritairComiteId: '' }, ...prev]);
    revealRow(id);
  }, [setFunctionExemptions, revealRow]);

  const onDeleteRow = useCallback(
    (id: number) => {
      setFunctionExemptions(prev => prev.filter(c => c.id !== id));
    },
    [setFunctionExemptions],
  );

  const getRowSeverity = useCallback(
    (row: IFunctionExemption): RowSeverity | undefined => (rowErrors.rowIds.includes(row.id) ? 'error' : undefined),
    [rowErrors],
  );

  const hasFieldError = useCallback(
    (row: IFunctionExemption, field: WageExemptionField) =>
      rowErrors.fields.includes(field) && rowErrors.rowIds.includes(row.id),
    [rowErrors],
  );

  const functionCellRenderer = useCallback(
    ({ row }: { row: IFunctionExemption }) => (
      <UPDropdown
        label=""
        value={row.functionId ? row.functionId.toString() : ''}
        items={functionOptions}
        placeholder={t(GENERAL__SELECT)}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'key')}
        onChange={e => updateFunctionExemption(row.id, 'functionId', e.target.value)}
      />
    ),
    [functionOptions, t, userIsCompanyManager, updateFunctionExemption, hasFieldError],
  );

  const paritairCellRenderer = useCallback(
    ({ row }: { row: IFunctionExemption }) => (
      <UPDropdown
        label=""
        value={row.paritairComiteId ?? ''}
        items={paritairComites ?? []}
        placeholder={t(GENERAL__SELECT)}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'jointCommission')}
        onChange={e => updateFunctionExemption(row.id, 'paritairComiteId', e.target.value)}
      />
    ),
    [paritairComites, t, userIsCompanyManager, updateFunctionExemption, hasFieldError],
  );

  const wageCellRenderer = useCallback(
    ({ row }: { row: IFunctionExemption }) => (
      <WageInputCell
        id={row.id}
        value={row.wageExemption}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'wage')}
        onChange={onWageChange}
      />
    ),
    [userIsCompanyManager, onWageChange, hasFieldError],
  );

  const deleteCellRenderer = useCallback(
    ({ row }: { row: IFunctionExemption }) => (
      <DeleteActionCell id={row.id} disabled={userIsCompanyManager} onDelete={onDeleteRow} />
    ),
    [userIsCompanyManager, onDeleteRow],
  );

  const columns = useMemo<UPTanStackTableColumn<IFunctionExemption>[]>(
    () => [
      { id: 'functionId', header: t(COMPANY_PAYROLL__FUNCTION), size: 220, cell: functionCellRenderer },
      { id: 'paritairComiteId', header: t(COMPANY_PAYROLL__PARITAIR_COMITE), size: 220, cell: paritairCellRenderer },
      { id: 'wageExemption', header: t(COMPANY_PAYROLL__WAGE_EXEMPTION), size: 160, cell: wageCellRenderer },
      { id: 'startDate', header: t(WAGE_INDEXATION__VALID_FROM), accessorKey: 'startDate', size: 140 },
      { id: 'endDate', header: t(WAGE_INDEXATION__VALID_TO), accessorKey: 'endDate', size: 140 },
      { id: 'actions', header: '', size: 60, align: 'center', headerAlign: 'center', cell: deleteCellRenderer },
    ],
    [t, functionCellRenderer, paritairCellRenderer, wageCellRenderer, deleteCellRenderer],
  );

  return (
    <Box>
      <Box
        sx={{
          marginTop: '32px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
          <UPButton
            text={`+ ${t(COMPANY_PAYROLL__ADD_NEW_FUNCTION_EXEMPTION)}`}
            onClick={onAddNewFunctionWageExemption}
            disabled={userIsCompanyManager}
          />
          <Box display="flex" alignItems="center">
            <CustomSearchBox onSearch={onSearchChange} />
          </Box>
        </Box>
      </Box>
      <UPTanStackTable
        data={filteredRows}
        columns={columns}
        getRowId={getRowId}
        getRowSeverity={getRowSeverity}
        enableSorting={false}
        pagination={pagination}
        onPaginationChange={setPagination}
        autoResetPageIndex={false}
        scrollToRowId={scrollToRowId}
        emptyMessage={t(DATAGRID__NO_ROWS)}
        sx={{ height: 'auto', '& .MuiTableContainer-root': { maxHeight: '500px' } }}
      />
    </Box>
  );
};
export default FunctionExemptionsGrid;
